import { useState, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import AddButton from "../../../../../components/AddButton";
import EditButton from "../../../../../components/EditButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import {
  addStateAgencyMapping,
  editStateAgencyMapping,
  rowForEditAgencyMapping,
  selectedRowsToDelete,
} from "../atoms/atoms";
import { useGetAgencyMapping } from "../hooks/useGetAgencyMapping";
import { useDeactivateAgencyMapping } from "../hooks/useDeactivateAgencyMapping";
import { AgencyMappingForm } from "../forms/AgencyMappingForm";
import { useHistory, useLocation } from "react-router-dom";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";

import useDeleteMaguireAgencyMapping from "../hooks/useDeleteMaguireAgencyMapping";
interface Props {}
export const AgencyMappingTable: React.FC<Props> = () => {
  const [agencyMappingList, setAgencyMappingList] = useState([]);
  //Hooks Call
  const {
    data: agencyMappingData,
    isLoading: isFetching,
    isSuccess: agencyMappingSuccess,
  } = useGetAgencyMapping();

  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  useEffect(() => {
    if (agencyMappingSuccess && agencyMappingData) {
      if (agencyMappingData?.status === 200) {
        setAgencyMappingList(
          agencyMappingData.data.map((data) => ({ ...data }))
        );
      }
    }
  }, [agencyMappingData, agencyMappingSuccess]);

  const [addState, setAddState] = useRecoilState(addStateAgencyMapping);
  const [editState, setEditState] = useRecoilState(editStateAgencyMapping);
  const [selectedRows, setSelectedRows] = useRecoilState(selectedRowsToDelete);
  const setRowToEdit = useSetRecoilState(rowForEditAgencyMapping);
  const [filterText, setFilterText] = useState("");
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);
  const [agencyMappingFilteredData, setAgencyMappingFilteredData] = useState(
    []
  );

  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_agencymapping_add;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_agencymapping_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_agencymapping_delete;

  const BulkDeleteAgencyMapping = useDeactivateAgencyMapping();
  const { mutateAsync: deleteMaguireAgencyMapping } =
    useDeleteMaguireAgencyMapping();

  //For filtered data update
  const [agencyMapId, setAgencyMapId] = useState("");
  useEffect(() => {
    if (agencyMapId?.includes(".")) {
      let ids = agencyMapId.split(".");
      const arr = ids.map((id) => Number(id));
      const filteredData = agencyMappingData?.data.filter((x) =>
        arr.includes(x.agencyMappingId)
      );
      setAgencyMappingFilteredData(filteredData);
    } else {
      const filteredData = agencyMappingData?.data.filter(
        (x) => x.agencyMappingId === Number(agencyMapId)
      );
      setAgencyMappingFilteredData(filteredData);
    }
  }, [agencyMapId, agencyMappingData]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setAgencyMappingFilteredData(result);
          setAgencyMapId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  //Filter
  useEffect(() => {
    if (filterText) {
      setAgencyMappingList(
        agencyMappingData?.data.filter((x) =>
          x.agencyStandard
            .toLowerCase()
            .includes(filterText?.trim().toLowerCase())
        )
      );
    } else {
      setAgencyMappingList(agencyMappingData?.data);
    }
  }, [filterText, agencyMappingData?.data]);

  const handleEdit = (rowDetails) => {
    setRowToEdit({ ...rowDetails, isActive: !rowDetails.isActive });
    setEditState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Agency Mappings" : "Agency Mapping"
    }?`}</div>
  );

  useEffect(() => {
    setSelectedRows([]);
  }, []);

  const handleDelete = () => {
    const ids = selectedRows
      ? selectedRows.map((row) => row.agencyMappingId)
      : undefined;
    BulkDeleteAgencyMapping(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    deleteMaguireAgencyMapping(ids);
  };

  const handleAdd = () => {
    setAddState(true);
  };

  const matColumns = [
    {
      title: "Agency Standard",
      field: "agencyStandard",
      customSort: (a, b) => {
        // Handle null/undefined
        const standardA = a.agencyStandard?.toLowerCase().trim() || "";
        const standardB = b.agencyStandard?.toLowerCase().trim() || "";

        // If agency is empty, consider it as the smallest value (comes last in descending order)
        if (standardA > standardB) return 1;
        if (standardA < standardB) return -1;
        return 0;
      },
    },
    {
      title: "Agency Source",
      field: "agencySource",
      customSort: (a, b) => {
        // Handle null/undefined
        const sourceA = a.agencySource?.toLowerCase().trim() || "";
        const sourceB = b.agencySource?.toLowerCase().trim() || "";

        // If agency is empty, consider it as the smallest value (comes last in descending order)
        if (sourceA > sourceB) return 1;
        if (sourceA < sourceB) return -1;
        return 0;
      },
    },
    {
      title: "State",
      field: "state",
    },
    {
      title: "Agency Name",
      field: "agencyName",
      customSort: (a, b) => {
        // Handle null/undefined
        const agencyNameA = a.agencyName?.toLowerCase().trim() || "";
        const agencyNameB = b.agencyName?.toLowerCase().trim() || "";

        // If agency is empty, consider it as the smallest value (comes last in descending order)
        if (agencyNameA > agencyNameB) return 1;
        if (agencyNameA < agencyNameB) return -1;
        return 0;
      },
    },
    {
      title: "Created By",
      field: "createdBy",
    },
    {
      title: "Created Date",
      field: "createdOn",
      dateSetting: { locale: "en-US", format: "MM/dd/yyyy" },
      render: (rowData) =>
        rowData?.createdOn ? (
          <div className="">
            <div>{new Date(rowData?.createdOn).toLocaleDateString()}</div>
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Modified By",
      field: "updatedBy",
    },
    {
      title: "Modified Date",
      field: "updatedOn",
      dateSetting: { locale: "en-US", format: "MM/dd/yyyy" },
      render: (rowData) =>
        rowData?.updatedOn ? (
          <div className="">
            <div>{new Date(rowData?.updatedOn).toLocaleDateString()}</div>
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: alignCenter,
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex justify-items-center mr-5">
            <EditButton
              className={""}
              disabled={
                editState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {addState || editState ? (
          <div>
            <div></div>
            <AgencyMappingForm
              isAdd={addState}
              isEdit={editState}
              setIsAdd={setAddState}
              setIsEdit={setEditState}
            />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                moduleDetails={"agencyStandard"}
                textInputName={"AgencyStandard"}
                columnDetails={"Agency Standard"}
                filterText={setFilterText}
                disabledButton={
                  editState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
                disabledTextBox={
                  editState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New Agency Mapping"
                      onClick={() => handleAdd()}
                      disabled={
                        editState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>

            <div>
              <MaterialTableTS
                data={
                  agencyMappingFilteredData?.length > 0
                    ? agencyMappingFilteredData
                    : agencyMappingList
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={
                  !editState &&
                  !addState &&
                  userPermissions.includes(deletePermission)
                }
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Agency Mapping"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
};
